// src/components/IngredientPicker.jsx — ohapotato v2026.10.36
import React, { useState } from 'react';
import { STAPLES } from '../data/ingredients';
import CategoryFilter    from './CategoryFilter';
import IngredientSearch  from './IngredientSearch';
import IngredientTile    from './IngredientTile';
import SelectedChips     from './SelectedChips';
import FindButton        from './FindButton';

export default function IngredientPicker({
  pantry = new Set(), onTogglePantry,
  selected, filtered, selectedList,
  category, search,
  onToggle, onRemove, onClear,
  onCategoryChange, onSearchChange,
  onFind, loading,
  nonStapleCount,
}) {
  const [staplesOpen, setStaplesOpen] = useState(false);

  return (
    <div className="picker-panel">
      <div style={styles.panelHeader}>
        <div style={styles.panelStepBadge}>Step 1</div>
        <h2 style={styles.title}>What's in your kitchen?</h2>
        <p style={styles.subtitle}>
          Pick 2 or more ingredients beyond your staples.
        </p>
      </div>

      {/* ── Staples section ───────────────────────── */}
      <div style={{...styles.staplesWrap, cursor:"pointer"}} onClick={() => setStaplesOpen(o => !o)}>
        <div style={styles.staplesToggle}>
          <span style={styles.staplesToggleLeft}>
            <span style={styles.staplesIcon}>🧂</span>
            <span style={styles.staplesLabel}>Kitchen staples</span>
            <span style={styles.staplesCount}>
              {STAPLES.filter(s => selected.has(s.id)).length}/{STAPLES.length} selected
            </span>
          </span>
          <span style={styles.staplesChevron}>{staplesOpen ? '▴' : '▾'}</span>
        </div>

        {staplesOpen && (
          <div style={styles.staplesGrid}>
            {STAPLES.map(ing => (
              <div
                key={ing.id}
                onClick={e => { e.stopPropagation(); onToggle(ing.id); }}
                style={{
                  ...styles.stapleTile,
                  background: selected.has(ing.id)
                    ? 'var(--accent-green-bg)' : 'var(--bg-base)',
                  border: selected.has(ing.id)
                    ? '1px solid var(--accent-green)' : '1px solid var(--border-base)',
                  opacity: selected.has(ing.id) ? 1 : 0.5,
                }}
              >
                {selected.has(ing.id) && (
                  <span style={styles.stapleCheck}>✓</span>
                )}
                <span style={styles.stapleTileIcon}>{ing.emoji}</span>
                <span style={{
                  ...styles.stapleTileLabel,
                  color: selected.has(ing.id) ? 'var(--accent-green)' : 'var(--text-muted)',
                }}>{ing.label}</span>
              </div>
            ))}
          </div>
        )}

        {!staplesOpen && (
          <div style={styles.staplesPreview}>
            {STAPLES.filter(s => selected.has(s.id)).map(s => (
              <span key={s.id} style={styles.staplesPreviewChip}>{s.emoji}</span>
            ))}
            {STAPLES.filter(s => !selected.has(s.id)).length > 0 && (
              <span style={styles.staplesRemovedNote}>
                {STAPLES.filter(s => !selected.has(s.id)).length} removed
              </span>
            )}
          </div>
        )}
      </div>

      <div style={styles.divider} />

      <IngredientSearch value={search} onChange={onSearchChange} />
      <CategoryFilter active={category} onChange={onCategoryChange} />

      <div className="ingredient-grid">
        {filtered.length === 0 ? (
          <p style={styles.noResults}>No ingredients match "{search}"</p>
        ) : (
          filtered.map(ing => (
            <IngredientTile
              inPantry={pantry.has(ing.id)}
              key={ing.id}
              ingredient={ing}
              isSelected={selected.has(ing.id)}
              onToggle={onToggle}
            />
          ))
        )}
      </div>

      <div style={styles.divider} />

      <div style={styles.selectedSection}>
        <span style={styles.selectedLabel}>Selected</span>
        <SelectedChips selectedList={selectedList} onRemove={onRemove} onClear={onClear} />
      </div>

      <div className="desktop-find-btn" style={{ marginTop: '12px' }}>
        <FindButton
          count={selected.size}
          nonStapleCount={nonStapleCount}
          onClick={onFind}
          loading={loading}
        />
      </div>
    </div>
  );
}

const styles = {
  panelHeader: { marginBottom: '14px' },
  panelStepBadge: {
    display: 'inline-block',
    fontSize: '10px', fontWeight: '700', letterSpacing: '1px',
    textTransform: 'uppercase',
    color: 'var(--accent-orange)',
    background: 'var(--accent-orange-bg)',
    padding: '3px 10px', borderRadius: 'var(--radius-pill)',
    fontFamily: 'var(--font-body)',
    marginBottom: '8px',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '18px', fontWeight: '700', letterSpacing: '-0.3px',
    color: 'var(--text-primary)', marginBottom: '3px',
    transition: 'color 0.25s ease',
  },
  subtitle: {
    fontSize: '13px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)',
    transition: 'color 0.25s ease',
  },

  // ── Staples ──────────────────────────────────────────
  staplesWrap: {
    background: 'var(--bg-base)',
    border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '12px',
    overflow: 'hidden',
    transition: 'border-color 0.15s ease',
  },
  staplesToggle: {
    width: '100%', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    padding: '11px 14px',
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    transition: 'background 0.12s ease',
    borderRadius: 'var(--radius-md)',
  },
  staplesToggleLeft: {
    display: 'flex', alignItems: 'center', gap: '7px',
  },
  staplesIcon: { fontSize: '16px', lineHeight: 1 },
  staplesLabel: {
    fontSize: '14px', fontWeight: '600',
    color: 'var(--text-secondary)',
  },
  staplesCount: {
    fontSize: '12px', color: 'var(--text-muted)',
    background: 'var(--bg-pill)',
    padding: '2px 8px', borderRadius: 'var(--radius-pill)',
  },
  staplesChevron: {
    fontSize: '11px', color: 'var(--text-muted)',
  },

  staplesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '6px',
    padding: '6px 12px 12px',
  },
  stapleTile: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: '4px', padding: '9px 6px',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.12s ease',
    minHeight: '66px',
  },
  stapleCheck: {
    position: 'absolute', top: '3px', right: '4px',
    fontSize: '9px', fontWeight: '700',
    color: 'var(--accent-green)',
  },
  stapleTileIcon: { fontSize: '22px', lineHeight: 1 },
  stapleTileLabel: {
    fontSize: '11.5px', fontWeight: '500',
    textAlign: 'center', lineHeight: '1.2',
    fontFamily: 'var(--font-body)',
    transition: 'color 0.12s ease',
  },

  staplesPreview: {
    display: 'flex', alignItems: 'center',
    gap: '4px', padding: '0 12px 8px',
    flexWrap: 'wrap',
  },
  staplesPreviewChip: {
    fontSize: '18px', lineHeight: 1,
  },
  staplesRemovedNote: {
    fontSize: '11px', color: 'var(--accent-orange)',
    fontFamily: 'var(--font-body)',
    background: 'var(--accent-orange-bg)',
    padding: '1px 7px', borderRadius: 'var(--radius-pill)',
  },

  // ── Rest ─────────────────────────────────────────────
  divider: {
    height: '1px', background: 'var(--border-base)',
    margin: '12px 0', transition: 'background 0.25s ease',
  },
  noResults: {
    gridColumn: '1 / -1', textAlign: 'center',
    padding: '24px', color: 'var(--text-muted)',
    fontSize: '13px', fontStyle: 'italic',
  },
  selectedSection: { marginBottom: '4px' },
  selectedLabel: {
    display: 'block',
    fontSize: '11px', fontWeight: '600',
    letterSpacing: '1px', textTransform: 'uppercase',
    color: 'var(--text-muted)', marginBottom: '9px',
    transition: 'color 0.25s ease',
  },
};
